import React, { useState } from 'react';
import { GovernanceColors } from '@/config/governanceColors';
import { useAuditData } from '@/hooks/useCommandCenter';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Download, ChevronLeft, ChevronRight, Lock, Anchor, Layers, ChevronDown, ChevronUp } from 'lucide-react';

function PipelineEvidence({ evidence }: { evidence: any }) {
  const stages = evidence?.findings || evidence?.stages || [];
  if (!stages.length) return null;
  const stageColors: Record<string, string> = { pass: GovernanceColors.healthy, flag: GovernanceColors.warning, halt: GovernanceColors.critical };
  return (
    <div className="mt-2 p-3 rounded bg-[#1C1917] border border-[#2E2A26]">
      <div className="flex items-center gap-2 mb-2">
        <Layers className="w-3 h-3" style={{ color: GovernanceColors.primaryLight }} />
        <span className="text-xs font-medium" style={{ color: GovernanceColors.primaryLight }}>Pipeline Evidence Chain</span>
        {evidence.chainHash && (
          <span className="text-[10px] font-mono text-[#8F857B] ml-auto">{evidence.chainHash.substring(0, 24)}...</span>
        )}
      </div>
      <div className="flex gap-1">
        {stages.map((s: any, i: number) => (
          <div key={i} className="flex-1 text-center p-1 rounded border border-[#2E2A26] bg-[#292524]">
            <div className="text-[10px] font-mono" style={{ color: stageColors[s.status] || '#A8A29E' }}>
              {s.stage ?? i + 1}
            </div>
            <div className="text-[8px] text-[#8F857B] leading-tight">{s.name || ''}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AuditView() {
  const { entries, integrity, anchors, page, totalPages, setPage, loading } = useAuditData();
  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  const integrityPct = integrity?.integrityPercentage ?? integrity?.integrity ?? 100;
  const blockCount = integrity?.totalBlocks ?? integrity?.blockCount ?? entries.length;
  const anchorCount = anchors?.anchors?.length ?? anchors?.count ?? 0;

  const handleExport = () => {
    window.open('/api/audit/export?limit=10000&format=json', '_blank');
  };

  if (loading) {
    return <div className="text-[#A8A29E] text-sm">Loading audit data...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Audit Trail</h2>
        <Button
          variant="outline"
          size="sm"
          className="border-[#4A443D] text-[#A8A29E] hover:text-[#F5F0E8] hover:bg-[#3D3833]"
          onClick={handleExport}
        >
          <Download className="w-3 h-3 mr-2" />
          Export JSON
        </Button>
      </div>

      {/* Badges */}
      <div className="flex gap-3">
        <Badge variant="outline" style={integrityPct === 100 ? { color: GovernanceColors.healthy, borderColor: GovernanceColors.healthy } : { color: GovernanceColors.warning, borderColor: GovernanceColors.warning }}>
          {integrityPct}% Integrity
        </Badge>
        <Badge variant="outline" style={{ color: GovernanceColors.streaming, borderColor: GovernanceColors.streaming }}>
          {blockCount} Blocks
        </Badge>
        <Badge variant="outline" className="text-[#A8A29E] border-[#4A443D]">
          {anchorCount} Anchors
        </Badge>
      </div>

      {/* Status cards */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="bg-[#292524] border-[#3A352F]">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Lock className="w-4 h-4" style={{ color: GovernanceColors.healthy }} />
              <span className="text-sm font-medium">Chain Integrity</span>
            </div>
            <span className="text-2xl font-bold" style={{ color: integrityPct === 100 ? GovernanceColors.healthy : GovernanceColors.warning }}>
              {integrityPct}%
            </span>
            <p className="text-xs text-[#8F857B] mt-1">Hash-linked, tamper-evident</p>
          </CardContent>
        </Card>
        <Card className="bg-[#292524] border-[#3A352F]">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Anchor className="w-4 h-4" style={{ color: GovernanceColors.streaming }} />
              <span className="text-sm font-medium">Hash Anchors</span>
            </div>
            <span className="text-2xl font-bold">{anchorCount}</span>
            <p className="text-xs text-[#8F857B] mt-1">External checkpoints</p>
          </CardContent>
        </Card>
      </div>

      {/* Audit entries table */}
      <Card className="bg-[#292524] border-[#3A352F]">
        <CardContent className="p-4">
          <h3 className="text-sm font-medium mb-3">Audit Entries</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#3A352F] text-[#A8A29E]">
                  <th className="text-left py-2 px-3 font-medium">Block</th>
                  <th className="text-left py-2 px-3 font-medium">Timestamp</th>
                  <th className="text-left py-2 px-3 font-medium">Agent</th>
                  <th className="text-left py-2 px-3 font-medium">Action</th>
                  <th className="text-left py-2 px-3 font-medium">Tier</th>
                  <th className="text-left py-2 px-3 font-medium">Hash</th>
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-4 text-center text-[#8F857B]">No audit entries found.</td>
                  </tr>
                ) : (
                  entries.map((entry: any, i: number) => {
                    const hasPipeline = entry.metadata?.evidenceChainHash || entry.metadata?.pipelineStages || entry.evidenceChainHash;
                    const pipelineData = entry.metadata || entry;
                    return (
                      <React.Fragment key={i}>
                        <tr
                          className={`border-b border-[#2E2A26] hover:bg-[#3D3833] ${hasPipeline ? 'cursor-pointer' : ''}`}
                          onClick={() => hasPipeline && setExpandedRow(expandedRow === i ? null : i)}
                        >
                          <td className="py-2 px-3 font-mono text-xs">
                            {hasPipeline && (
                              expandedRow === i
                                ? <ChevronUp className="w-3 h-3 inline mr-1" style={{ color: GovernanceColors.primaryLight }} />
                                : <ChevronDown className="w-3 h-3 inline mr-1" style={{ color: GovernanceColors.primaryLight }} />
                            )}
                            {entry.blockId ?? entry.chainPosition ?? i}
                          </td>
                          <td className="py-2 px-3 text-xs text-[#A8A29E]">
                            {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : '-'}
                          </td>
                          <td className="py-2 px-3 text-xs">{entry.capsule ?? entry.agentId ?? '-'}</td>
                          <td className="py-2 px-3 text-xs">{entry.action ?? entry.message ?? '-'}</td>
                          <td className="py-2 px-3 text-xs">
                            <Badge variant="outline" className="text-xs px-1 py-0 text-[#A8A29E] border-[#4A443D]">
                              {entry.tier ?? entry.accessLevel ?? '-'}
                            </Badge>
                          </td>
                          <td className="py-2 px-3 font-mono text-xs text-[#8F857B]">
                            {(entry.hash ?? entry.inputHash ?? entry.metadata?.evidenceChainHash ?? '').substring(0, 16)}
                          </td>
                        </tr>
                        {expandedRow === i && hasPipeline && (
                          <tr className="bg-[#1C1917]">
                            <td colSpan={6} className="px-3 pb-3">
                              <PipelineEvidence evidence={pipelineData} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
                className="border-[#4A443D] text-[#A8A29E]"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-xs text-[#A8A29E]">Page {page} of {totalPages}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
                className="border-[#4A443D] text-[#A8A29E]"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
